import * as Sentry from "@sentry/vue";
import { isAxiosError } from "axios";
import { createApp, nextTick } from "vue";
import { createPinia } from "pinia";
import { PiniaColada } from "@pinia/colada";
import "@fontsource/barlow/latin.css";
import "@fontsource/open-sans/latin.css";
import { Quasar, Notify, Dialog, AppFullscreen } from "quasar";
import "quasar/src/css/index.sass";
import "quasar/src/css/flex-addon.sass";
import "@quasar/extras/material-icons/material-icons.css";
import "@quasar/extras/material-icons-outlined/material-icons-outlined.css";
import "@quasar/extras/material-icons-round/material-icons-round.css";

import App from "./App.vue";
import router from "./router";
// @ts-ignore
import createStore from "./store";
import "@/scss/main.scss";

import {
  initVersionCheck,
  isChunkLoadError,
  promptReloadForNewVersion,
} from "@/utils/version";
import { axiosErrorFingerprint, HandledError } from "./utils/errors";
import { PiniaColadaAutoRefetch } from "@pinia/colada-plugin-auto-refetch";

initVersionCheck();

const app = createApp(App);

app.config.errorHandler = (error, vm, info) => {
  if (error instanceof HandledError) {
    return;
  }
  console.error(
    `Unhandled error in component ${vm?.$options.__name} -> ${info}:`,
    error,
    vm,
  );
  Sentry.captureException(error, {
    extra: {
      component: vm?.$options.name,
      errorInfo: info,
    },
  });
};

app.use(router);

router.beforeEach((to, from) => {
  if (to.query.login_error && to.name !== "logged-out") {
    return { name: "logged-out", query: { login_error: to.query.login_error } };
  }
});

router.onError((error) => {
  if (isChunkLoadError(error) && promptReloadForNewVersion()) return;
  throw error;
});

router.afterEach((to, from) => {
  // https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609
  nextTick(() => {
    if (to.meta.title) {
      document.title = to.meta.title.toString();
    }
  });
});

export const store = createStore();
store.router = router;
app.config.globalProperties.$actions = store.actions;
app.config.globalProperties.$state = store.state;

const pinia = createPinia();
pinia.use(({ store }) => {
  store.router = router;
});

app.use(pinia);
app.use(PiniaColada, {
  queryOptions: {
    gcTime: 300000, // 5 minutes
  },
  plugins: [PiniaColadaAutoRefetch({ autoRefetch: false })], // disable global auto refetch; enable per query basis
});

app.use(Quasar, {
  plugins: {
    Notify,
    Dialog,
    AppFullscreen,
  },
  config: {
    brand: {
      primary: "#fff252",
      secondary: "#467eb6",
      dark: "#151515",
      "dark-page": "#151515",
    },
    dark: false,
    screen: {
      bodyClasses: true,
    },
  },
});

if (import.meta.env.PROD && !import.meta.env.STAGING) {
  Sentry.init({
    app,
    dsn: "https://69b3225e02d54305a83f232c89979f05@o1243369.ingest.sentry.io/6398708",
    environment: "production",
    beforeSend(event, hint) {
      const err = hint.originalException;
      if (isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 401 || status === 403) return null;
        event.fingerprint = axiosErrorFingerprint(err);
      }
      return event;
    },
    integrations: [],
    replaysSessionSampleRate: 0.2,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/.*\.yeply\.((com)|(fi)|(de)|(nl)).*/,
    ],
    profilesSampleRate: 1.0,
  });
}

import("@sentry/vue").then((lazyLoadedSentry) => {
  Sentry.addIntegration(
    lazyLoadedSentry.replayIntegration({
      networkDetailAllowUrls: [window.location.origin],
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
    }),
  );
  Sentry.addIntegration(
    lazyLoadedSentry.vueIntegration({
      tracingOptions: {
        trackComponents: true,
      },
    }),
  );
  Sentry.addIntegration(lazyLoadedSentry.browserProfilingIntegration());
  Sentry.addIntegration(lazyLoadedSentry.browserTracingIntegration({ router }));
});

app.mount("#app");
